import { useContext } from "react";
import { useSelector } from "react-redux";
import { useReactFlow } from "reactflow";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import { Button } from "@mui/material";
import useGetBranchNodes from "hooks/useGetBranchNodes";
import { addLabelNodes } from "layouts/Flow Designer/helpers/common";
import {
  ComponentPropsContext,
  ComponentPropsContextApi,
  TabContextApi,
} from "layouts/Flow Designer/context";
import useComponentIdGenerator from "hooks/useComponentIdGenerator";
import useFlowStepConnection from "hooks/useFlowStepConnection";
import useFitView from "hooks/useFitView";

const NewBranchButton = ({ tabId, formRef, allowEditWorkflow }) => {
  const tabState = useSelector(
    (store) => store?.designer?.componentPropsPanel?.tabs?.[tabId] || {}
  );
  const { selectedNode, flowElement } = tabState;
  const elementId = selectedNode?.elementId || flowElement?.elementId || "";
  const { generateNodeId } = useComponentIdGenerator();
  const { actionForm } = useContext(ComponentPropsContext);
  const { setActionForm, setInitialActionForm } = useContext(ComponentPropsContextApi);
  const { autoSaveFlow } = useContext(TabContextApi);
  const { getMergeNodeId } = useGetBranchNodes();
  const { addHiddenBranchNodes } = useFlowStepConnection();
  const { handleFitView } = useFitView();
  const reactFlowInstance = useReactFlow();
  const { getNode, getNodes, getEdges, setNodes, setEdges, screenToFlowPosition } =
    reactFlowInstance;
  const mergeNodeId = getMergeNodeId(getNode(elementId));
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const isFlowActive = currentSavedFlow?.shared_status === "active";

  const handleAddBranchButtonClick = () => {
    try {
      addNewBranch();
    } catch (error) {
      console.error(error);
    }
  };

  const addNewBranch = () => {
    if ( !elementId ) return

    const node = getNode(elementId);
    const branchName = `Case ${actionForm.branches.length}`;
    const { x, y } = node?.position ?? {};
    const position = screenToFlowPosition({
      x: x,
      y: y,
    });
    const labelStepElements = getLabelStepElements(node, branchName, position);
    const hiddenBranchData = addHiddenBranchNodes(
      labelStepElements,
      position,
      mergeNodeId,
      mergeNodeId
    );
    const labelNodeId = addLabelNodeAndEdgesToCanvas(hiddenBranchData);

    if (labelNodeId) {
      updateActionForm(labelNodeId, branchName);
    }

    autoSaveFlow(reactFlowInstance);
    scrollDownForm();
  };

  const getLabelStepElements = (node, branchName, position) => {
    const params = {
      position,
      branchName,
      getId: generateNodeId,
      sourceId: elementId,
      targetId: mergeNodeId,
    };
    return addLabelNodes(params);
  };

  const addLabelNodeAndEdgesToCanvas = ({ labelNodes = [], labelEdges = [] }) => {
    setNodes([...getNodes(), ...labelNodes]);
    setEdges([...getEdges(), ...labelEdges]);

    handleFitView();

    if (labelNodes.length) {
      return labelNodes[0].id;
    }
    return "";
  };

  const updateActionForm = (labelNodeId, branchName) => {
    const key = `${elementId}-${branchName.replace(/ /g, "").toLowerCase()}`;
    const newBranch = {
          name: branchName,
          id: labelNodeId,
          key: key,
          label: branchName,
          value: "",
          endPoint: mergeNodeId,
        }
    setActionForm({
      ...actionForm,
      branches: [
        ...actionForm.branches,
        newBranch,
      ],
    });
    setInitialActionForm(prev => ({
      ...prev,
      branches: [
        ...prev.branches,
        { ...newBranch }
      ]
    }))
  };

  const scrollDownForm = () => {
    setTimeout(() => {
      formRef.current.scrollTo({
        top: formRef.current?.scrollHeight,
        left: 0,
        behavior: "smooth",
      });
    }, 20);
  };

  return (
    <SoftBox sx={{ display: "flex", justifyContent: "center" }}>
      {!isFlowActive && allowEditWorkflow && (
        <SoftButton
          component={Button}
          name="addBranchButton"
          color="primary"
          variant="outlined"
          sx={{ marginTop: "24px" }}
          onClick={handleAddBranchButtonClick}
        >
          Add Branch
        </SoftButton>
      )}
    </SoftBox>
  );
};

export default NewBranchButton;
